import React, { useEffect, useState, useRef } from 'react';
import { Drawer, Box, IconButton, Toolbar, Checkbox } from '@mui/material';
import { ChevronLeft as ChevronLeftIcon } from '@mui/icons-material';
import FileUpload from './FileUpload';
import { ReactComponent as CloseIcon } from '../assets/Close.svg';
import { ReactComponent as ForwardIcon } from '../assets/Forward.svg';
import { ReactComponent as BackwardIcon } from '../assets/Backward.svg';
import { ReactComponent as ScreenshotIcon } from '../assets/Screenshot.svg';
import { ReactComponent as SaveIcon } from '../assets/Save.svg';
import { ReactComponent as SearchIcon } from '../assets/Search.svg';
import { ReactComponent as UndoIcon } from '../assets/Undo.svg';
import { ReactComponent as RedoIcon } from '../assets/Redo.svg';
import { ReactComponent as DriveIcon } from '../assets/Drive.svg';
import { ReactComponent as DownloadIcon } from '../assets/Download.svg';
import { handleSavePattern, applyPattern } from '../utils/apiUtils';

function PDFViewer({
  state,
  stateData,
  currentPage,
  totalPages,
  changePage,
  isRedactionMode,
  handleRedactStart,
  savePDF,
  isLoading,
  canvasRef,
  overlayCanvasRef,
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
  handleMouseLeave,
  handleUndo,
  handleRedo,
  currentHistoryNode,
  STATE_NOTHING,
  STATE_PDF_DISPLAYED,
  sessionsDrawerOpen,
  setSessionsDrawerOpen,
  patternsDrawerOpen,
  setPatternsDrawerOpen,
  files,
  setFiles,
  patterns,
  handleUpload,
  showPDF,
  handleDelete,
  fetchPatternsData,
  setToast,
}) {
  
  const hasRedactions = Object.keys(stateData.redactions || {}).some(pageNum => 
    stateData.redactions[pageNum] && stateData.redactions[pageNum].length > 0
  );
  const drawerWidth = 350;

  // Debug logging for undo/redo states
  console.log('PDFViewer render - currentHistoryNode:', currentHistoryNode);
  console.log('Has previous:', currentHistoryNode?.previous ? 'yes' : 'no');
  console.log('Has next:', currentHistoryNode?.next ? 'yes' : 'no');

  // State to track which dates are expanded
  const [expandedDates, setExpandedDates] = useState({});
  const [patternName, setPatternName] = useState('');
  // State for selected files
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  // State for drag and drop
  const [isDragOver, setIsDragOver] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchIndex, setSearchIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const fileInputRef = useRef(null);

  const handleDrawerClose = () => {
    setSessionsDrawerOpen(false);
  };

  const handlePatternsDrawerClose = () => {
    setPatternsDrawerOpen(false);
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf') {
        const fakeEvent = {
          preventDefault: () => {},
          target: {
            elements: {
              file: { files: [file] }
            }
          }
        };
        handleUpload(fakeEvent);
      } else {
        alert('Please upload only PDF files.');
      }
    }
  };

  const handleDropzoneClick = () => {
    fileInputRef.current?.click();
  };

  const runSearch = async (query) => {
    const trimmed = (query || '').trim();
    if (!trimmed || state !== STATE_PDF_DISPLAYED || !stateData?.currentPdfId) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    setSearchError('');
    try {
      const res = await fetch('/api/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file_id: stateData.currentPdfId,
          query: trimmed,
          fuzzy: true,
          threshold: 0.78,
          max_results: 12,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || 'Search failed');
      }
      const matches = Array.isArray(data.matches) ? data.matches : [];
      setSearchResults(matches);
      setSearchIndex(0);
      if (matches.length > 0) {
        const targetPage = matches[0].page;
        changePage(targetPage - currentPage);
        setToast?.({ type: 'success', message: `Found ${matches.length} match(es). Jumped to page ${targetPage + 1}.` });
      } else {
        setToast?.({ type: 'warn', message: 'No matches found.' });
      }
    } catch (err) {
      console.error('Search failed', err);
      setSearchError(err.message || 'Search failed');
      setToast?.({ type: 'error', message: err.message || 'Search failed' });
    } finally {
      setIsSearching(false);
    }
  };

  const goToSearchResult = (nextIndex) => {
    if (!searchResults.length) return;
    const bounded = (nextIndex + searchResults.length) % searchResults.length;
    setSearchIndex(bounded);
    const targetPage = searchResults[bounded].page;
    changePage(targetPage - currentPage);
  };

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      const formElement = e.target.closest('form');
      const syntheticEvent = {
        preventDefault: () => {},
        target: formElement
      };
      handleUpload(syntheticEvent);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleUpload(e);
  };

  // Function to toggle date expansion
  const toggleDateExpansion = (date) => {
    setExpandedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  // Function to handle file selection
  const handleFileSelect = (fileId, isSelected) => {
    setSelectedFiles(prev => {
      const newSelected = new Set(prev);
      if (isSelected) {
        newSelected.add(fileId);
      } else {
        newSelected.delete(fileId);
      }
      return newSelected;
    });
  };

  const handleSelectAllForDate = (dateFiles, isSelected) => {
    setSelectedFiles(prev => {
      const newSelected = new Set(prev);
      dateFiles.forEach(file => {
        if (isSelected) {
          newSelected.add(file.id);
        } else {
          newSelected.delete(file.id);
        }
      });
      return newSelected;
    });
  };

  const areAllFilesSelectedForDate = (dateFiles) => {
    return dateFiles.every(file => selectedFiles.has(file.id));
  };

  const areSomeFilesSelectedForDate = (dateFiles) => {
    return dateFiles.some(file => selectedFiles.has(file.id)) && !areAllFilesSelectedForDate(dateFiles);
  };

  const getTargetFiles = () => {
    if (selectedFiles.size > 0) {
      return files.filter(file => selectedFiles.has(file.id));
    }
    if (stateData?.currentPdfId) {
      const current = files.find(f => String(f.id) === String(stateData.currentPdfId));
      if (current) return [current];
      // Fallback: allow actions on the currently open file even if it isn't in the sidebar list.
      return [{
        id: stateData.currentPdfId,
        title: stateData?.pdfPath ? stateData.pdfPath.split('/').pop() : `file-${stateData.currentPdfId}.pdf`,
      }];
    }
    return [];
  };

  const handleBulkDelete = async () => {
    const targets = getTargetFiles();
    if (targets.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${targets.length} file(s)?`)) {
      try {
        // Delete files one by one
        for (const file of targets) {
          await handleDelete(file.id);
        }
        // Clear selected files after deletion
        setSelectedFiles(new Set());
        setToast && setToast({ type: 'success', message: `${targets.length} file(s) deleted.` });
      } catch (error) {
        console.error('Error during bulk delete:', error);
        setToast && setToast({ type: 'error', message: 'Delete failed. Please try again.' });
      }
    }
  };

  // Function to handle bulk save to Google Drive
  const handleBulkSaveToDrive = async () => {
    const targets = getTargetFiles();
    if (targets.length === 0) return;

    try {
      for (const file of targets) {
        await handleSaveToDrive(file);
      }
      
      // Clear selected files after saving
      setSelectedFiles(new Set());
    } catch (error) {
      console.error('Error during bulk save to Drive:', error);
    }
  };

  // Function to handle bulk download
  const handleBulkDownload = async () => {
    const targets = getTargetFiles();
    if (targets.length === 0) return;

    try {
      for (const file of targets) {
        // Fetch first so we can report failures in-app instead of browser download errors.
        const downloadUrl = new URL(`/api/redacted/id/${file.id}`, window.location.origin).toString();
        const response = await fetch(downloadUrl, {
          method: 'GET',
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error(`Could not download "${file.title || file.id}" (HTTP ${response.status})`);
        }
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = file.title || 'download.pdf';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
        
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      setSelectedFiles(new Set());
      setToast && setToast({ type: 'success', message: `${targets.length} file(s) downloaded.` });
    } catch (error) {
      console.error('Error during bulk download:', error);
      setToast && setToast({ type: 'error', message: error.message || 'Download failed. Please try again.' });
    }
  };

  const truncateFilename = (filename, maxLength = 35) => {
    const safeName = filename || 'Untitled';
    if (safeName.length <= maxLength) {
      return safeName;
    }
    return safeName.substring(0, maxLength) + '...';
  };

  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  };

  const isCurrentWeek = (dateString) => {
    const fileDate = new Date(dateString);
    const startOfWeek = getStartOfWeek(new Date());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    return fileDate >= startOfWeek && fileDate <= endOfWeek;
  };

  const handleSaveToDrive = async (file) => {
    try {
      const response = await fetch('/api/drive/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          file_id: file.id,
          title: file.title 
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        console.log(`File "${file.title}" saved to Google Drive successfully!`);
      } else if (data.needs_reauth) {
        const frontendOrigin = encodeURIComponent(window.location.origin);
        const authPath = data.auth_url || '/api/google/login?drive=1';
        const separator = authPath.includes('?') ? '&' : '?';
        window.location.href = `${authPath}${separator}frontend_origin=${frontendOrigin}`;
      } else {
        console.error(`Failed to save to Google Drive: ${data.error || 'Unknown error'}`);
        if (data.details) {
          console.error('Drive save error details:', data.details);
        }
      }
    } catch (error) {
      console.error('Error saving to Google Drive:', error);
    }
  };

  const sessionsByDate = files.reduce((acc, file) => {
    const dateSource = file.uploadDate || (file.metadata && file.metadata.created_at) || new Date().toISOString();
    const date = new Date(dateSource).toLocaleDateString();
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(file);
    return acc;
  }, {});

  const recentSessions = {};
  const historySessions = {};

  Object.entries(sessionsByDate).forEach(([date, files]) => {
    if (isCurrentWeek(date)) {
      recentSessions[date] = files;
    } else {
      historySessions[date] = files;
    }
  });

  const SessionsContent = () => (
    <Box sx={{ width: drawerWidth, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <h3 className="font-medium text-lg">Sessions</h3>
          <IconButton onClick={handleDrawerClose} size="small">
            <ChevronLeftIcon />
          </IconButton>
        </Box>
        
        <FileUpload isLoading={isLoading} handleUpload={handleUpload} />
      </Box>
      
      {/* Scrollable sessions list */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2 }}>
        <div className="sessions-list">
          <h3 className="font-medium text-lg mb-2">Recent Sessions</h3>
          {Object.entries(recentSessions).length > 0 ? (
            // Sort dates in descending order (most recent first) before mapping
            Object.entries(recentSessions)
              .sort(([dateA], [dateB]) => {
                // Convert string dates back to Date objects for comparison
                return new Date(dateB) - new Date(dateA);
              })
              .map(([date, sessionFiles]) => (
                <div key={date} className="session-group">
                  <div 
                    className="session-date" 
                    onClick={() => toggleDateExpansion(date)}
                    style={{ 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={areAllFilesSelectedForDate(sessionFiles)}
                      indeterminate={areSomeFilesSelectedForDate(sessionFiles)}
                      onChange={(e) => handleSelectAllForDate(sessionFiles, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ 
                        color: 'white',
                        '&.Mui-checked': { color: '#ff9600' },
                        '&.MuiCheckbox-indeterminate': { color: '#ff9600' }
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      {date} ({sessionFiles.length} PDF{sessionFiles.length !== 1 ? 's' : ''})
                      <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                        {expandedDates[date] ? '▼' : '▶'}
                      </span>
                    </div>
                  </div>
                  {expandedDates[date] && (
                    <div className="session-files">
                      {(sessionFiles || []).filter(Boolean).map(file => (
                        <div key={file.id} className="session-file-row">
                          <Checkbox
                            size="small"
                            checked={selectedFiles.has(file.id)}
                            onChange={(e) => handleFileSelect(file.id, e.target.checked)}
                            sx={{ 
                              color: 'white',
                              '&.Mui-checked': { color: '#ff9600' }
                            }}
                          />
                          <div 
                            className="session-file" 
                            title={file?.title || 'Untitled'}
                            onClick={() => {
                              setSelectedFiles(new Set([file.id]));
                              showPDF && showPDF(file);
                            }}
                          >
                            {truncateFilename(file?.title || 'Untitled')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
          ) : (
            <p className="text-sm text-gray-500">No recent sessions</p>
          )}
        </div>

        {Object.entries(historySessions).length > 0 && (
          <div className="sessions-list">
            <h3 className="font-medium text-lg mb-2">History</h3>
            {Object.entries(historySessions)
              .sort(([dateA], [dateB]) => {
                // Convert string dates back to Date objects for comparison
                return new Date(dateB) - new Date(dateA);
              })
              .map(([date, sessionFiles]) => (
                <div key={date} className="session-group">
                  <div 
                    className="session-date" 
                    onClick={() => toggleDateExpansion(date)}
                    style={{ 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={areAllFilesSelectedForDate(sessionFiles)}
                      indeterminate={areSomeFilesSelectedForDate(sessionFiles)}
                      onChange={(e) => handleSelectAllForDate(sessionFiles, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ 
                        color: 'white',
                        '&.Mui-checked': { color: '#ff9600' },
                        '&.MuiCheckbox-indeterminate': { color: '#ff9600' }
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      {date} ({sessionFiles.length} PDF{sessionFiles.length !== 1 ? 's' : ''})
                      <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                        {expandedDates[date] ? '▼' : '▶'}
                      </span>
                    </div>
                  </div>
                  {expandedDates[date] && (
                    <div className="session-files">
                      {(sessionFiles || []).filter(Boolean).map(file => (
                        <div key={file.id} className="session-file-row">
                          <Checkbox
                            size="small"
                            checked={selectedFiles.has(file.id)}
                            onChange={(e) => handleFileSelect(file.id, e.target.checked)}
                            sx={{ 
                              color: 'white',
                              '&.Mui-checked': { color: '#ff9600' }
                            }}
                          />
                          <div 
                            className="session-file" 
                            title={file?.title || 'Untitled'}
                            onClick={() => {
                              setSelectedFiles(new Set([file.id]));
                              showPDF && showPDF(file);
                            }}
                          >
                            {truncateFilename(file?.title || 'Untitled')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}
      </Box>
      
      {/* Fixed action icons at bottom */}
      <Box sx={{ p: 2, borderTop: '1px solid #555', flexShrink: 0 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}>
          {/* Selected files text above */}
          <div className="selected-files-text">
            {selectedFiles.size === 0 ? (
              <span>No files selected</span>
            ) : (
              <span>{selectedFiles.size} file{selectedFiles.size !== 1 ? 's' : ''} selected</span>
            )}
          </div>
          
          {/* Action icons centered */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 3 
          }}>
            {/** Determine if actions should be active (selected files or open PDF) */}
            {(() => {
              const hasCurrent = Boolean(stateData?.currentPdfId);
              const hasSelected = selectedFiles.size > 0;
              const canAct = hasSelected || hasCurrent;
              const actionStyle = {
                opacity: canAct ? 1 : 0.3,
                cursor: canAct ? 'pointer' : 'not-allowed'
              };
              return (
                <>
                  <button
                    type="button"
                    className="bottom-action-icon"
                    onClick={() => {
                      if (canAct) {
                        handleBulkSaveToDrive();
                        return;
                      }
                      setToast && setToast({ type: 'info', message: 'Open or select a file first.' });
                    }}
                    title={
                      hasSelected
                        ? `Save ${selectedFiles.size} selected file(s) to Google Drive`
                        : hasCurrent
                          ? 'Save current file to Google Drive'
                          : 'Select or open a file first'
                    }
                    style={actionStyle}
                    disabled={!canAct}
                  >
                    <DriveIcon />
                    {selectedFiles.size > 0 && (
                      <span className="action-count">{selectedFiles.size}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    className="bottom-action-icon"
                    onClick={() => {
                      if (canAct) {
                        handleBulkDownload();
                        return;
                      }
                      setToast && setToast({ type: 'info', message: 'Open or select a file first.' });
                    }}
                    title={
                      hasSelected
                        ? `Download ${selectedFiles.size} selected file(s)`
                        : hasCurrent
                          ? 'Download current file'
                          : 'Select or open a file first'
                    }
                    style={actionStyle}
                    disabled={!canAct}
                  >
                    <DownloadIcon />
                    {selectedFiles.size > 0 && (
                      <span className="action-count">{selectedFiles.size}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    className="bottom-action-icon"
                    onClick={() => {
                      if (canAct) {
                        handleBulkDelete();
                        return;
                      }
                      setToast && setToast({ type: 'info', message: 'Open or select a file first.' });
                    }}
                    title={
                      hasSelected
                        ? `Delete ${selectedFiles.size} selected file(s)`
                        : hasCurrent
                          ? 'Delete current file'
                          : 'Select or open a file first'
                    }
                    style={actionStyle}
                    disabled={!canAct}
                  >
                    <CloseIcon />
                    {selectedFiles.size > 0 && (
                      <span className="action-count">{selectedFiles.size}</span>
                    )}
                  </button>
                </>
              );
            })()}
          {/*
            <div 
              className="bottom-action-icon"
              onClick={handleBulkSaveToDrive}
              title={`Save ${selectedFiles.size} selected file(s) to Google Drive`}
              style={{
                opacity: selectedFiles.size === 0 ? 0.3 : 1,
                cursor: selectedFiles.size === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              <DriveIcon />
              {selectedFiles.size > 0 && (
                <span className="action-count">{selectedFiles.size}</span>
              )}
            </div>
            <div 
              className="bottom-action-icon"
              onClick={handleBulkDownload}
              title={`Download ${selectedFiles.size} selected file(s)`}
              style={{
                opacity: selectedFiles.size === 0 ? 0.3 : 1,
                cursor: selectedFiles.size === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              <DownloadIcon />
              {selectedFiles.size > 0 && (
                <span className="action-count">{selectedFiles.size}</span>
              )}
            </div>
            <div 
              className="bottom-action-icon"
              onClick={handleBulkDelete}
              title={`Delete ${selectedFiles.size} selected file(s)`}
              style={{
                opacity: selectedFiles.size === 0 ? 0.3 : 1,
                cursor: selectedFiles.size === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              <CloseIcon />
              {selectedFiles.size > 0 && (
                <span className="action-count">{selectedFiles.size}</span>
              )}
            </div>
          */}  
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const PatternsContent = () => (
    <Box sx={{ width: drawerWidth, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <h3 className="font-medium text-lg">Redaction Patterns</h3>
        <IconButton onClick={handlePatternsDrawerClose} size="small">
                      <ChevronLeftIcon />
        </IconButton>
      </Box>
      
      <div className="patterns-list">
        <h3 className="font-medium text-lg mb-2">Saved Patterns</h3>
        {patterns && patterns.length > 0 ? (
          <div className="flex flex-col gap-2">
            {patterns.map((pattern) => (
              <div key={pattern} className="flex items-center gap-2">
                <button
                  className="bg-gray-200 px-3 py-1 rounded hover:bg-blue-400 hover:text-white"
                  onClick={async () => {
                    if (applyPattern) {
                      const result = await applyPattern(pattern, stateData, currentPage);
                      console.log('Pattern applied result:', stateData)
                      if (result && result.success && result.data) {
                        const filename = result.data.filename || (result.data.redactedPdfUrl || '').split('/').pop();
                        const userIdFromState = stateData.userId 
                          || (stateData.pdfPath ? stateData.pdfPath.split('/')[1] : null) 
                          || '0';
                        const relativePath = result.data.relative_pdf_path || `pdf/${userIdFromState}/${filename}`;

                        // Add the redacted PDF to the sidebar list
                        const newFile = {
                          id: result.data.file_id || stateData.currentPdfId || null,
                          title: filename,
                          path: relativePath,
                          pages: stateData.pages,
                        };
                        setFiles(prevFiles => [newFile, ...prevFiles]); // Add to top of list
                        console.log("here",newFile)

                        // Show the redacted PDF in the viewer
                        if (showPDF) showPDF(newFile);
                        setToast && setToast({ type: 'success', message: `Applied pattern '${pattern}'` });
                      } else {
                        const message = (result && result.error) ? result.error : 'Failed to apply pattern';
                        setToast && setToast({ type: 'error', message });
                        console.error('Failed to apply pattern', message);
                      }
                    }
                  }}
                >
                  {pattern}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No patterns saved yet</p>
        )}
      </div>
      
      <div className="mt-4 flex gap-2 items-center">
        <input
          type="text"
          placeholder="Pattern name"
          value={patternName}
          onChange={e => setPatternName(e.target.value)}
          className="border px-2 py-1 text-sm rounded"
        />
        <button
          className="text-sm bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600 transition-colors"
          onClick={async () => {
            if (patternName.trim()) {
              const res = await handleSavePattern(patternName.trim(), stateData, currentPage);
              if (res && res.success !== false) {
                setToast && setToast({ type: 'success', message: `Pattern '${patternName.trim()}' saved` });
                setPatternName('');
                await fetchPatternsData();
              } else {
                const message = res && res.error ? res.error : 'Failed to save pattern';
                setToast && setToast({ type: 'error', message });
              }
            }
          }}
        >
          Create New Pattern
        </button>
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Tip: draw at least a 5×5 px box before saving. If a pattern is missing or too small, you’ll see an error toast.
      </p>

      
    </Box>
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('auth') === 'drive_success') {
      setTimeout(() => {
        console.log('🎉 Google Drive access granted!');
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 500);
    }
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div 
        className="pdf-viewer" 
        style={{ 
          position: 'relative',
          minHeight: 'calc(100vh - 224px)', 
          height: 'calc(100vh - 224px)'
        }}
      >


        {state === STATE_PDF_DISPLAYED && (
          <div 
            className="pdf-container"
            style={{
              position: 'relative',
              overflow: 'auto',
              background: 'var(--canvas-bg)',
              border: '1px solid var(--canvas-border)',
              borderRadius: '22px',
              boxShadow: 'var(--panel-shadow)',
              backdropFilter: 'blur(6px)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              padding: '36px',
              margin: '0 auto',
              width: '100%',
              maxWidth: 'min(1400px, 96vw)',
              minHeight: 'calc(100vh - 260px)',
              height: 'auto'
            }}
          >
            <div style={{ 
              position: 'relative',
              boxShadow: '0 24px 50px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.05)',
              backgroundColor: 'white',
              margin: '0 auto',
              display: 'inline-block'
            }}>
              <canvas 
                ref={canvasRef}
                style={{
                  display: 'block',
                  maxWidth: '100%',
                  height: 'auto'
                }}
              />
              <div 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  cursor: isRedactionMode ? 'crosshair' : 'default',
                  pointerEvents: 'auto', 
                  zIndex: 5
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
              >
                <canvas
                  ref={overlayCanvasRef}
                  className="overlay-canvas"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    pointerEvents: 'none',
                    background: 'transparent'
                  }}
                />
              </div>
            </div>
          </div>
        )}
        {state === STATE_NOTHING && (
          <div 
            className={`upload-dropzone ${isDragOver ? 'drag-over' : ''} ${isLoading ? 'loading' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleDropzoneClick}
            style={{
              position: 'absolute',
              top: '-1.5rem',
              left: '-1.5rem',
              right: '-1.5rem',
              bottom: '-1.5rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              border: '2px dashed var(--canvas-border)',
              borderRadius: '18px',
              background: 'var(--canvas-bg)',
              transition: 'all 0.3s ease'
            }}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              height="80px" 
              viewBox="0 -960 960 960" 
              width="80px" 
              fill="var(--accent)" 
              className="upload-icon"
              style={{
                marginBottom: '24px',
                transition: 'all 0.3s ease'
              }}
            >
              <path d="M260-160q-91 0-155.5-63T40-377q0-78 47-139t123-78q25-92 100-149t170-57q117 0 198.5 81.5T760-520q69 8 114.5 59.5T920-340q0 75-52.5 127.5T740-160H520q-33 0-56.5-23.5T440-240v-206l-64 62-56-56 160-160 160 160-56 56-64-62v206h220q42 0 71-29t29-71q0-42-29-71t-71-29h-60v-80q0-83-58.5-141.5T480-720q-83 0-141.5 58.5T280-520h-20q-58 0-99 41t-41 99q0 58 41 99t99 41h100v80H260Zm220-280Z"/>
            </svg>
            <div className="upload-text" style={{ textAlign: 'center' }}>
              <p 
                className="upload-primary-text"
                style={{
                  fontSize: '24px',
                  fontWeight: '500',
                  color: '#e7eefc',
                  margin: '0 0 8px 0',
                  lineHeight: '1.2'
                }}
              >
                {isLoading ? 'Uploading...' : 'Drop your PDF here or click to browse'}
              </p>
              <p 
                className="upload-secondary-text"
                style={{
                  fontSize: '18px',
                  color: '#a8b6cc',
                  margin: '0',
                  lineHeight: '1.2'
                }}
              >
                PDF files only
              </p>
            </div>
            
            <form onSubmit={handleFormSubmit} style={{ display: 'none' }}>
              <input 
                ref={fileInputRef}
                type="file" 
                name="file" 
                accept=".pdf" 
                required 
                onChange={handleFileChange}
              />
            </form>
          </div>
        )}

        {/* Material-UI Persistent Drawer for Sessions */}
        <Drawer
          variant="persistent"
          anchor="left"
          open={sessionsDrawerOpen}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              position: 'absolute',
              top: '0',
              height: '100%',
              border: 'none',
              boxShadow: '2px 0 8px rgba(0, 0, 0, 0.15)',
              zIndex: 3,
              overflow: 'auto',
              backgroundColor: 'var(--panel)',
              color: 'white',
            },
          }}
          ModalProps={{
            container: () => document.querySelector('.pdf-viewer'),
            style: { position: 'absolute' },
            keepMounted: true
          }}
        >
          <SessionsContent />
        </Drawer>

        {/* Material-UI Persistent Drawer for Patterns */}
        <Drawer
          variant="persistent"
          anchor="right"
          open={patternsDrawerOpen}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              position: 'absolute',
              top: '0',
              height: '100%',
              border: 'none',
              boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
              zIndex: 3,
              overflow: 'auto',
              backgroundColor: 'var(--panel)',
              color: 'white',
            },
          }}
          ModalProps={{
            container: () => document.querySelector('.pdf-viewer'),
            style: { position: 'absolute' },
            keepMounted: true
          }}
        >
          <PatternsContent />
        </Drawer>
      </div>

      {/* Bottom Toolbar with Page Controls */}
      <div
        style={{
          width: '80%', 
          margin: '0 auto', 
          position: 'relative'
        }}
      >
        <Toolbar
          sx={{
            backgroundColor: 'var(--panel)',
            borderTop: '1px solid #e0e0e0',
            boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.1)',
            minHeight: '64px !important',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            padding: '0 24px',
            margin: 0
          }}
        >
          {/* Left side - Search */}
          <Box className="pdf-search-bar" sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <SearchIcon className="pdf-search-icon" style={{ width: '20px', height: '20px', fill: '#B7B7B7' }} />
            <input
              type="text"
              placeholder="Search in document..."
              value={searchQuery}
              className="pdf-search-input"
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  runSearch(searchQuery);
                }
              }}
              onFocus={(e) => {
                e.target.classList.add('is-focused');
              }}
              onBlur={(e) => {
                e.target.classList.remove('is-focused');
              }}
            />
            <button
              type="button"
              onClick={() => runSearch(searchQuery)}
              disabled={isSearching}
              className="pdf-search-btn"
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
            {searchResults.length > 1 && (
              <div className="pdf-search-nav">
                <button
                  type="button"
                  onClick={() => goToSearchResult(searchIndex - 1)}
                  className="pdf-search-nav-btn"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() => goToSearchResult(searchIndex + 1)}
                  className="pdf-search-nav-btn"
                >
                  Next
                </button>
              </div>
            )}
            {searchResults.length > 0 && (
              <span className="pdf-search-count">
                {searchIndex + 1}/{searchResults.length}
              </span>
            )}
            {searchError && (
              <span className="pdf-search-error">{searchError}</span>
            )}
          </Box>

          {/* Center - Page Controls (absolutely positioned for perfect centering) */}
          <Box 
            sx={{ 
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex', 
              alignItems: 'center', 
              gap: 2 
            }}
          >
            <IconButton 
              onClick={() => changePage(-1)} 
              disabled={state !== STATE_PDF_DISPLAYED || currentPage === 0}
              size="small"
              sx={{ 
                '&:hover': {
                  backgroundColor: '#f1eee1'
                }
              }}
            >
              <BackwardIcon />
            </IconButton>
            <span style={{ fontSize: '16px', color: 'white', whiteSpace: 'nowrap', minWidth: 90, textAlign: 'center', display: 'inline-block' }}>
              {state === STATE_PDF_DISPLAYED ? `Page ${currentPage + 1} of ${totalPages}` : 'Page - of -'}
            </span>
            <IconButton 
              onClick={() => changePage(1)} 
              disabled={state !== STATE_PDF_DISPLAYED || currentPage === totalPages - 1}
              size="small"
              sx={{ 
                '&:hover': {
                  backgroundColor: '#f1eee1'
                }
              }}
            >
              <ForwardIcon />
            </IconButton>
          </Box>

          {/* Right side - Action Buttons and Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180, justifyContent: 'flex-end', flex: 1 }}>
            {/* Undo button (always rendered, hidden if not in redaction mode) */}
            <IconButton 
              onClick={() => {
                if (isRedactionMode) handleUndo();
              }}
              size="small"
              disabled={!isRedactionMode || !currentHistoryNode || !currentHistoryNode.previous}
              sx={{ 
                borderRadius: '4px',
                visibility: isRedactionMode ? 'visible' : 'hidden',
                '&:hover': {
                  backgroundColor: '#f1eee1'
                },
                '&:disabled': {
                  backgroundColor: 'transparent',
                  opacity: 0.3
                }
              }}
              title="Undo"
            >
              <UndoIcon />
            </IconButton>
            {/* Redo button (always rendered, hidden if not in redaction mode) */}
            <IconButton 
              onClick={() => {
                if (isRedactionMode) handleRedo();
              }}
              size="small"
              disabled={!isRedactionMode || !currentHistoryNode || !currentHistoryNode.next}
              sx={{ 
                borderRadius: '4px',
                visibility: isRedactionMode ? 'visible' : 'hidden',
                '&:hover': {
                  backgroundColor: '#f1eee1'
                },
                '&:disabled': {
                  backgroundColor: 'transparent',
                  opacity: 0.3
                }
              }}
              title="Redo"
            >
              <RedoIcon />
            </IconButton>
            {/* Redact button (always rendered, hidden if PDF not displayed) */}
            <IconButton 
              onClick={handleRedactStart}
              size="small"
              sx={{ 
                border: isRedactionMode ? '2px solid #4285f4' : '1px solid #e0e0e0',
                borderRadius: '4px',
                backgroundColor: isRedactionMode ? '#4285f4' : 'transparent',
                color: isRedactionMode ? 'white' : 'inherit',
                visibility: state === STATE_PDF_DISPLAYED ? 'visible' : 'hidden',
                '&:hover': {
                  backgroundColor: isRedactionMode ? '#3367d6' : '#f1eee1'
                }
              }}
            >
              <ScreenshotIcon />
            </IconButton>
            {/* Save button (always rendered, hidden if PDF not displayed) */}
            <IconButton 
              onClick={savePDF} 
              disabled={isLoading || !hasRedactions}
              size="small"
              sx={{ 
                border: '1px solid #28a745',
                borderRadius: '4px',
                backgroundColor: '#28a745',
                color: 'white',
                visibility: state === STATE_PDF_DISPLAYED ? 'visible' : 'hidden',
                '&:hover': {
                  backgroundColor: '#218838'
                },
                '&:disabled': {
                  backgroundColor: '#6c757d',
                  borderColor: '#6c757d',
                  color: 'white',
                  opacity: 0.6
                }
              }}
            >
              <SaveIcon />
            </IconButton>
            <div 
              style={{ 
                width: '12px', 
                height: '12px', 
                borderRadius: '50%', 
                backgroundColor: state === STATE_PDF_DISPLAYED ? '#28a745' : '#dc3545',
                display: 'inline-block'
              }}
              title={state === STATE_PDF_DISPLAYED ? 'Ready' : 'No file selected'}
            />
          </Box>
        </Toolbar>
      </div>
    </div>
  );
}

export default PDFViewer;
